// src/GDB/components/ChartColorPicker.jsx
/**
 * Universal Chart Color Picker Component
 * Uses ReactDOM.createPortal to render modal outside parent container
 * This ensures the modal is not affected by parent CSS (overflow, z-index, etc.)
 */

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

// ============================================================
// Color Palettes
// ============================================================

export const COLOR_PALETTES = {
  default: {
    name: "Default",
    colors: ["#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"],
  },
  professional: {
    name: "Professional",
    colors: ["#1e40af", "#0f766e", "#b91c1c", "#a16207", "#6b21a8", "#0e7490", "#166534", "#9a3412"],
  },
  vibrant: {
    name: "Vibrant",
    colors: ["#f43f5e", "#8b5cf6", "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#14b8a6", "#eab308"],
  },
  pastel: {
    name: "Pastel",
    colors: ["#93c5fd", "#fca5a5", "#86efac", "#fde047", "#c4b5fd", "#f9a8d4", "#67e8f9", "#fdba74"],
  },
  ocean: {
    name: "Ocean",
    colors: ["#0ea5e9", "#06b6d4", "#14b8a6", "#0d9488", "#0891b2", "#0284c7", "#0369a1", "#075985"],
  },
  sunset: {
    name: "Sunset",
    colors: ["#f97316", "#f59e0b", "#eab308", "#ef4444", "#dc2626", "#f43f5e", "#fb923c", "#fbbf24"],
  },
  forest: {
    name: "Forest",
    colors: ["#22c55e", "#16a34a", "#15803d", "#166534", "#14532d", "#84cc16", "#65a30d", "#4d7c0f"],
  },
  berry: {
    name: "Berry",
    colors: ["#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f472b6", "#7c3aed", "#6366f1", "#c084fc"],
  },
  monochrome: {
    name: "Monochrome",
    colors: ["#1f2937", "#374151", "#4b5563", "#6b7280", "#9ca3af", "#d1d5db", "#e5e7eb", "#f3f4f6"],
  },
  rainbow: {
    name: "Rainbow",
    colors: ["#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899"],
  },
};

// Extended color grid
export const EXTENDED_COLORS = [
  "#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c",
  "#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c",
  "#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207",
  "#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d",
  "#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e",
  "#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8",
  "#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce",
  "#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d",
];

// ============================================================
// Modal Content Component
// ============================================================

function ColorPickerModal({
  onClose,
  chartType,
  dataLabels,
  currentColors,
  onSave,
}) {
  const [selectedPalette, setSelectedPalette] = useState("default");
  const [colors, setColors] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [customColor, setCustomColor] = useState("#3b82f6");

  useEffect(() => {
    const colorsObj = {};
    dataLabels.forEach((label, idx) => {
      if (currentColors && currentColors[label]) {
        colorsObj[label] = currentColors[label];
      } else if (Array.isArray(currentColors) && currentColors[idx]) {
        colorsObj[label] = currentColors[idx];
      } else {
        colorsObj[label] = COLOR_PALETTES.default.colors[idx % 8];
      }
    });
    setColors(colorsObj);
  }, [currentColors, dataLabels]);

  const getChartTypeLabel = () => {
    switch (chartType) {
      case "pie":
      case "doughnut":
        return "Slices";
      case "bar":
      case "column":
        return "Bars";
      default:
        return "Items";
    }
  };

  const applyPalette = (paletteName) => {
    setSelectedPalette(paletteName);
    const palette = COLOR_PALETTES[paletteName];
    if (!palette) return;
    const newColors = {};
    dataLabels.forEach((label, idx) => {
      newColors[label] = palette.colors[idx % palette.colors.length];
    });
    setColors(newColors);
  };

  const updateColor = (label, color) => {
    setColors((prev) => ({ ...prev, [label]: color }));
    setSelectedPalette(null);
  };

  const handleSave = () => {
    onSave?.(colors);
    onClose?.();
  };

  // Styles object for cleaner JSX
  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 999999,
    },
    modal: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
      width: "520px",
      maxWidth: "95vw",
      maxHeight: "85vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    header: {
      padding: "16px 20px",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f9fafb",
    },
    title: {
      margin: 0,
      fontSize: "18px",
      fontWeight: 600,
      color: "#111827",
    },
    subtitle: {
      margin: "4px 0 0",
      fontSize: "13px",
      color: "#6b7280",
    },
    closeBtn: {
      background: "#e5e7eb",
      border: "none",
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      fontSize: "20px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#374151",
    },
    content: {
      flex: 1,
      overflow: "auto",
      padding: "20px",
    },
    section: {
      marginBottom: "20px",
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#374151",
      marginBottom: "12px",
    },
    paletteGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
    },
    paletteBtn: (isSelected) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      border: isSelected ? "2px solid #3b82f6" : "1px solid #d1d5db",
      borderRadius: "8px",
      background: isSelected ? "#eff6ff" : "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      color: "#374151",
    }),
    colorDot: (color) => ({
      width: "16px",
      height: "16px",
      borderRadius: "4px",
      backgroundColor: color,
    }),
    itemsList: {
      background: "#f3f4f6",
      borderRadius: "8px",
      padding: "8px",
      maxHeight: "160px",
      overflow: "auto",
    },
    itemRow: (isEditing) => ({
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 12px",
      background: isEditing ? "#dbeafe" : "#ffffff",
      borderRadius: "6px",
      marginBottom: "6px",
      border: isEditing ? "2px solid #3b82f6" : "1px solid #e5e7eb",
    }),
    itemLabel: {
      flex: 1,
      fontSize: "14px",
      color: "#374151",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    colorSwatch: (color, isEditing) => ({
      width: "36px",
      height: "36px",
      borderRadius: "6px",
      border: isEditing ? "3px solid #3b82f6" : "2px solid #d1d5db",
      backgroundColor: color,
      cursor: "pointer",
    }),
    pickerBox: {
      background: "#eff6ff",
      borderRadius: "8px",
      padding: "16px",
      border: "1px solid #bfdbfe",
    },
    colorGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(6, 1fr)",
      gap: "8px",
      marginBottom: "16px",
    },
    gridColor: (color, isSelected) => ({
      width: "100%",
      aspectRatio: "1",
      borderRadius: "6px",
      border: isSelected ? "3px solid #1f2937" : "2px solid transparent",
      backgroundColor: color,
      cursor: "pointer",
      boxSizing: "border-box",
    }),
    customRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      background: "#ffffff",
      padding: "10px",
      borderRadius: "6px",
    },
    footer: {
      padding: "16px 20px",
      borderTop: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f9fafb",
    },
    resetBtn: {
      padding: "10px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      cursor: "pointer",
      border: "1px solid #fca5a5",
      background: "#fef2f2",
      color: "#dc2626",
    },
    cancelBtn: {
      padding: "10px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      cursor: "pointer",
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#374151",
    },
    saveBtn: {
      padding: "10px 24px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      border: "none",
      background: "#3b82f6",
      color: "#ffffff",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>🎨 Customize Colors</h2>
            <p style={styles.subtitle}>
              {dataLabels.length} {getChartTypeLabel().toLowerCase()} to customize
            </p>
          </div>
          <button style={styles.closeBtn} onClick={onClose} type="button">
            ×
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Palettes */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Quick Palettes</div>
            <div style={styles.paletteGrid}>
              {Object.entries(COLOR_PALETTES).map(([key, palette]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyPalette(key)}
                  style={styles.paletteBtn(selectedPalette === key)}
                >
                  <div style={{ display: "flex", gap: "2px" }}>
                    {palette.colors.slice(0, 4).map((c, i) => (
                      <div key={i} style={styles.colorDot(c)} />
                    ))}
                  </div>
                  {palette.name}
                </button>
              ))}
            </div>
          </div>

          {/* Individual Items */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Individual {getChartTypeLabel()}</div>
            <div style={styles.itemsList}>
              {dataLabels.map((label, idx) => (
                <div key={idx} style={styles.itemRow(editingIndex === idx)}>
                  <span style={{ fontSize: "12px", color: "#9ca3af", width: "24px" }}>
                    {idx + 1}.
                  </span>
                  <span style={styles.itemLabel} title={label}>
                    {label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingIndex(editingIndex === idx ? null : idx)}
                    style={styles.colorSwatch(colors[label] || "#ccc", editingIndex === idx)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          {editingIndex !== null && dataLabels[editingIndex] && (
            <div style={styles.pickerBox}>
              <div style={{ ...styles.sectionTitle, color: "#1e40af", marginBottom: "12px" }}>
                🎯 Pick color for: {dataLabels[editingIndex]}
              </div>
              <div style={styles.colorGrid}>
                {EXTENDED_COLORS.map((color, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => updateColor(dataLabels[editingIndex], color)}
                    style={styles.gridColor(color, colors[dataLabels[editingIndex]] === color)}
                  />
                ))}
              </div>
              <div style={styles.customRow}>
                <span style={{ fontSize: "13px", color: "#4b5563" }}>Custom:</span>
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  style={{ width: "40px", height: "32px", border: "none", cursor: "pointer" }}
                />
                <input
                  type="text"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  style={{
                    width: "80px",
                    padding: "6px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "13px",
                  }}
                />
                <button
                  type="button"
                  onClick={() => updateColor(dataLabels[editingIndex], customColor)}
                  style={{
                    padding: "6px 14px",
                    background: "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            type="button"
            onClick={() => {
              applyPalette("default");
              setEditingIndex(null);
            }}
            style={styles.resetBtn}
          >
            Reset
          </button>
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} style={styles.saveBtn}>
              Save Colors
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Component with Portal
// ============================================================

export default function ChartColorPicker({
  isOpen,
  onClose,
  chartType,
  dataLabels = [],
  currentColors = {},
  onSave,
}) {
  if (!isOpen) return null;

  // Use createPortal to render modal at document.body level
  // This ensures it's not affected by parent container's CSS
  return createPortal(
    <ColorPickerModal
      onClose={onClose}
      chartType={chartType}
      dataLabels={dataLabels}
      currentColors={currentColors}
      onSave={onSave}
    />,
    document.body
  );
}

// ============================================================
// Utility Functions
// ============================================================

export function getChartColors(customColors = {}, dataLabels = [], paletteName = "default") {
  const palette = COLOR_PALETTES[paletteName] || COLOR_PALETTES.default;
  return dataLabels.map((label, idx) => {
    if (customColors && customColors[label]) return customColors[label];
    return palette.colors[idx % palette.colors.length];
  });
}

export function getSeriesColors(customColors = {}, seriesNames = [], paletteName = "default") {
  const palette = COLOR_PALETTES[paletteName] || COLOR_PALETTES.default;
  const result = {};
  seriesNames.forEach((name, idx) => {
    result[name] = (customColors && customColors[name]) || palette.colors[idx % palette.colors.length];
  });
  return result;
}